import { useCurrencySymbol } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shared/components/ui/shadcn/table';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import type { FormattedError } from '@shared/utils/RequestUtils';
import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useProfitabilityOrderDetail } from '../../hooks/useProfitabilityOrderDetail';
import { PROFITABILITY_DATE_COLUMN, type ProfitabilityCommonFilters } from '../../models/Profitability';
import { formatProfitabilityAmount } from '../../utils/formatMoney';

function isFormattedError(e: unknown): e is FormattedError {
  return (
    typeof e === 'object' &&
    e !== null &&
    'message' in e &&
    typeof (e as FormattedError).message === 'string'
  );
}

export const OrderDetailPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation('profitabilityModule');
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'es-ES';
  const currency = useCurrencySymbol();

  const { params: queryParams } = useQueryParams({
    start_date: { type: 'string', default: '' },
    end_date: { type: 'string', default: '' },
    shop_id: { type: 'number', default: null as number | null },
  });

  const idNum = orderId ? Number(orderId) : NaN;

  const commonFilters: ProfitabilityCommonFilters | undefined = useMemo(() => {
    if (!queryParams.start_date || !queryParams.end_date) return undefined;
    return {
      start_date: queryParams.start_date,
      end_date: queryParams.end_date,
      date_column: PROFITABILITY_DATE_COLUMN,
      shop_id: queryParams.shop_id ?? undefined,
    };
  }, [queryParams]);

  const q = useProfitabilityOrderDetail(
    Number.isFinite(idNum) ? idNum : undefined,
    commonFilters,
    Number.isFinite(idNum)
  );

  const listSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';

  const errMsg =
    q.error && isFormattedError(q.error) ? q.error.message : q.error ? t('errors.generic') : null;

  const order = q.data?.data;

  return (
    <div className="px-6 py-4 space-y-6" data-testid="profitability-order-detail">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" asChild className="border-card-border bg-card-bg">
          <Link to={`/reports/profitability${listSearch}`} replace={false}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('detail.back')}
          </Link>
        </Button>
        <h1 className="text-lg font-semibold text-title">{t('detail.title')}</h1>
      </div>

      {q.isLoading && <p className="text-sm text-complementary-text">…</p>}

      {errMsg && (
        <Alert variant="destructive" data-testid="profitability-order-detail-error">
          <AlertDescription>{errMsg}</AlertDescription>
        </Alert>
      )}

      {order && !q.isLoading && (
        <div className="space-y-6">
          <SectionBox data-testid="profitability-order-detail-summary">
            <h2 className="text-lg font-semibold text-title font-mono mb-4">{order.ref}</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
              <div>
                <span className="text-complementary-text">{t('orders.status')}: </span>
                <span className="text-title">
                  {t(`summary.order_status.${order.status_id}`, String(order.status_id))}
                </span>
              </div>
              <div>
                <span className="text-complementary-text">{t('orders.profit')}: </span>
                <span className="font-semibold tabular-nums text-title">
                  {formatProfitabilityAmount(order.order_profit, locale, currency)}
                </span>
              </div>
              {order.sum_line_cogs != null && (
                <div>
                  <span className="text-complementary-text">{t('detail.sum_line_cogs')}: </span>
                  <span className="tabular-nums text-title">{formatProfitabilityAmount(order.sum_line_cogs, locale, currency)}</span>
                </div>
              )}
              <div>
                <span className="text-complementary-text">{t('orders.logistics_source')}: </span>
                <span className="text-title">{order.logistics_source ?? '—'}</span>
              </div>
            </div>
          </SectionBox>

          <SectionBox data-testid="profitability-order-detail-lines">
            <h2 className="text-lg font-semibold text-title mb-4">{t('orders.lines')}</h2>
            <div className="overflow-x-auto rounded-lg border border-card-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-card-border">
                    <TableHead className="text-complementary-text">{t('orders.line_product')}</TableHead>
                    <TableHead className="text-right text-complementary-text">{t('orders.revenue')}</TableHead>
                    <TableHead className="text-right text-complementary-text">{t('orders.cogs')}</TableHead>
                    <TableHead className="text-right text-complementary-text">{t('orders.logistics')}</TableHead>
                    <TableHead className="text-right text-complementary-text">{t('orders.profit')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.lines.map((line) => (
                    <TableRow key={line.order_line_id} className="border-card-border">
                      <TableCell className="text-title">
                        {line.product_id != null
                          ? `#${line.product_id}`
                          : line.pack_id != null
                            ? `${t('orders.line_pack')} #${line.pack_id}`
                            : '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatProfitabilityAmount(line.allocated_revenue, locale, currency)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatProfitabilityAmount(line.line_cogs, locale, currency)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatProfitabilityAmount(line.allocated_logistics, locale, currency)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-title">
                        {formatProfitabilityAmount(line.line_profit, locale, currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </SectionBox>
        </div>
      )}
    </div>
  );
};
